import {
  HeadingNode,
  type HeadingTagType,
  type SerializedHeadingNode,
} from '@lexical/rich-text';
import {
  $applyNodeReplacement,
  $getState,
  $getStateChange,
  $setState,
  type EditorConfig,
  type ElementDOMSlot,
  type LexicalNode,
  type NodeKey,
  type ParagraphNode,
  type RangeSelection,
} from 'lexical';

import { createAsideDom } from '../../noteNodes';

import { templateSectionState } from './templateNodeState';

const HEADING_TAG_SELECTOR = ':scope > [data-note-heading-tag]';

const markSection = (dom: HTMLElement, node: TemplateHeadingNode) => {
  dom.dataset.sectionTitle =
    $getState(node, templateSectionState, 'direct')?.ID ?? '';
  return dom;
};

// A section of the template: its heading, which carries the section's id
// and settings.
export class TemplateHeadingNode extends HeadingNode {
  static getType(): string {
    return 'template-heading';
  }

  static clone(node: TemplateHeadingNode): TemplateHeadingNode {
    return new TemplateHeadingNode(node.__tag, node.__key);
  }

  static importJSON(json: SerializedHeadingNode): TemplateHeadingNode {
    return new TemplateHeadingNode(json.tag).updateFromJSON(json);
  }

  constructor(tag: HeadingTagType = 'h1', key?: NodeKey) {
    super(tag, key);
  }

  // The tag after the title is an element of its own, outside the title's
  // text, so what measures where the title ends counts the tag too.
  createDOM(config: EditorConfig): HTMLElement {
    const dom = markSection(super.createDOM(config), this);
    dom.append(createAsideDom('span', 'heading-tag'));
    return dom;
  }

  updateDOM(previous: this, dom: HTMLElement, config: EditorConfig): boolean {
    if ($getStateChange(this, previous, templateSectionState)) {
      markSection(dom, this);
    }
    return super.updateDOM(previous, dom, config);
  }

  getDOMSlot(element: HTMLElement): ElementDOMSlot<HTMLElement> {
    return super
      .getDOMSlot(element)
      .withBefore(element.querySelector(HEADING_TAG_SELECTOR));
  }

  // Lexical moves a title that Enter finds the caret at the start of into
  // a new heading, leaving an empty line in its place: the section goes
  // with its title.
  insertNewAfter(
    selection?: RangeSelection,
    restoreSelection = true,
  ): ParagraphNode | HeadingNode {
    const moves =
      !!selection && selection.anchor.offset === 0 && !this.isEmpty();
    const section = $getState(this, templateSectionState);
    const next = super.insertNewAfter(selection, restoreSelection);
    if (moves && $isTemplateHeadingNode(next)) {
      $setState(next, templateSectionState, section);
    }
    return next;
  }

  // Lexical replaces a heading Backspace finds the caret at the start of
  // the document in, which would drop its section.
  collapseAtStart(): true {
    return true;
  }
}

// A small tag after a heading's title, e.g. that AI scores an evaluation
// field; without a label it shows nothing.
export const markTemplateHeadingTag = (
  heading: HTMLElement,
  label?: string,
) => {
  const tag = heading.querySelector<HTMLElement>(HEADING_TAG_SELECTOR);
  if (!tag) {
    return;
  }
  if (label) {
    tag.dataset.label = label;
  } else {
    delete tag.dataset.label;
  }
};

export const $createTemplateHeadingNode = () =>
  $applyNodeReplacement(new TemplateHeadingNode('h1'));

export const $isTemplateHeadingNode = (
  node: LexicalNode | null | undefined,
): node is TemplateHeadingNode => node instanceof TemplateHeadingNode;
